import type { RadarData } from "@radarspotter/types";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { lazy, Suspense, useEffect, useState } from "react";
import "dayjs/locale/fr";

import InstallHint from "./components/InstallHint";
import NotifyButton from "./components/NotifyButton";
import RadarList from "./components/RadarList";
import Toggle from "./components/Toggle";
import { fetchData } from "./lib/api";

// La carte (maplibre-gl, ~800 Ko) n'est chargée qu'au passage sur l'onglet Carte.
const RadarMap = lazy(() => import("./components/RadarMap"));

dayjs.extend(relativeTime);
dayjs.locale("fr");

type Status = "loading" | "ready" | "error";

/**
 * Lien profond (ouvert depuis une notification) : `?radar=<id>` centre la carte
 * sur ce radar, `?carte` ouvre la carte. Le paramètre est retiré de l'URL.
 */
function readDeepLink() {
	const params = new URLSearchParams(window.location.search);
	const radar = params.get("radar");
	const map = radar !== null || params.has("carte");
	if (map) window.history.replaceState(null, "", window.location.pathname);
	return { map, radar };
}

function FullScreenMessage({ text }: { text: string }) {
	return <div className="flex h-[100dvh] items-center justify-center">{text}</div>;
}

function App() {
	const [deepLink] = useState(readDeepLink);
	const [data, setData] = useState<RadarData | null>(null);
	const [showMap, setShowMap] = useState(deepLink.map);
	const [focusId, setFocusId] = useState<string | null>(deepLink.radar);
	const [status, setStatus] = useState<Status>("loading");

	useEffect(() => {
		const controller = new AbortController();
		fetchData(controller.signal)
			.then((d) => {
				setData(d);
				setStatus("ready");
			})
			.catch((err) => {
				if (controller.signal.aborted) return;
				console.error("Erreur de chargement:", err);
				setStatus("error");
			});
		return () => controller.abort();
	}, []);

	function showOnMap(id: string) {
		setFocusId(id);
		setShowMap(true);
	}

	function toggleMap(map: boolean) {
		if (!map) setFocusId(null);
		setShowMap(map);
	}

	return (
		<div className="w-[600px] max-[600px]:w-screen select-none text-foreground">
			<InstallHint />
			<NotifyButton />
			{showMap ? (
				// La carte attend les données pour pouvoir centrer le radar ciblé.
				<Suspense fallback={<FullScreenMessage text="Chargement de la carte…" />}>
					{data ? (
						<RadarMap radars={data.radars} focusId={focusId} onCloseFocus={() => setFocusId(null)} />
					) : (
						<FullScreenMessage text={status === "error" ? "Impossible de charger les radars." : "Chargement…"} />
					)}
				</Suspense>
			) : (
				<RadarList data={data} status={status} onShowOnMap={showOnMap} />
			)}

			<Toggle checked={showMap} onChange={toggleMap} leftLabel="Liste" rightLabel="Carte" />
		</div>
	);
}

export default App;
